import React, { useState, Fragment } from 'react';
import { useTranslate, TextInput, FormDataConsumer, required } from 'react-admin';

import { EditButton, Drawer, BlockOrUnblockProfile } from '~/components';
import { BusinessLegalTypeInput, IndustryInput } from '~/resources/businesses/components';
import { MerchantCategoryCodeInput, EditForm, BranchSelect } from '~/components/ra';
import { Business } from '~/types/businesses';
import { useHistory } from 'react-router-dom';
import EditActions from '~/components/EditActions';
import UpdateRiskRating from '~/components/UpdateRiskRating';
import UpdateCustomerNumber from '~/components/UpdateCustomerNumber';
import KYCLevelForceUpdate from '~/components/KYC/KYCLevelForceUpdate';
import KYCLevelCheck from '~/components/KYC/KYCLevelCheck';
import KycResetProgress from '~/components/KYC/KycResetProgress';

type FormData = {
  formData: Business;
  [x: string]: any;
};

interface Props {
  record: Business;
  resource: string;
  permissions: string[];
}

const EditAccountInfo = (props: Props) => {
  const { record, resource, permissions } = props;
  const label = (key: string): string => `resources.businesses.fields.${key}`;
  const translate = useTranslate();
  const translateLabel = (key: string): string => translate(label(key));

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const history = useHistory();

  const menuItems = [
    <span key='edit'>
      <EditButton onClick={handleOpen} disabled={!permissions?.includes('business.update')} />
    </span>,
    permissions?.includes('business.block') && record?.status === 'ACTIVE' && (
      <BlockOrUnblockProfile
        key='block'
        profileId={record.id}
        profileType='BUSINESS'
        action='block'
      />
    ),
    permissions?.includes('business.unblock') && record?.status === 'BLOCKED' && (
      <BlockOrUnblockProfile
        key='unblock'
        profileId={record.id}
        profileType='BUSINESS'
        action='unblock'
      />
    ),
    permissions?.includes('kyc.workflow.start') && (
      <KYCLevelCheck key='kyc' record={record} resource={resource} permissions={permissions} />
    ),
    permissions?.includes('kyc.resetProgress') && (
      <KycResetProgress key='kyc-reset' record={record} resource={resource} />
    ),
    permissions?.includes('business.forceKYCLevelUpdate') && (
      <KYCLevelForceUpdate key='kyc-force-update' record={record} resource={resource} />
    ),
    permissions?.includes('business.updateRiskRating') && (
      <UpdateRiskRating key='update-risk' profileId={record?.id} profileType='BUSINESS' />
    ),
    permissions?.includes('business.updateCustomerNumber') && (
      <UpdateCustomerNumber
        key='update-customer-number'
        profileId={record?.id}
        profileType='BUSINESS'
      />
    ),
  ].filter(Boolean);
  return (
    <Fragment>
      <div>
        <EditActions menuItems={menuItems} />
      </div>
      <Drawer heading={translateLabel('edit.profileInfo')} open={open} onClose={handleClose}>
        <EditForm
          record={record}
          resource='businesses'
          withDelete={permissions?.includes('business.delete')}
          withDeleteConfirmation
          onDeleteSuccess={() => {
            history && history.push('/businesses');
          }}
        >
          <FormDataConsumer>
            {({ formData }: FormData) => {
              return (
                <Fragment>
                  <TextInput
                    source='legalName'
                    label={label('legalName')}
                    validate={required()}
                    fullWidth
                  />
                  <BusinessLegalTypeInput
                    solutionId={formData.solutionId}
                    country={formData.country}
                    source='legalType'
                    validate={required()}
                    label={label('legalType')}
                    disabled={!!record.legalType}
                    fullWidth
                  />
                  <TextInput source='tradeName' label={label('tradeName')} fullWidth />
                  <TextInput
                    source='tradeNameLocalized'
                    label={label('tradeNameLocalized')}
                    fullWidth
                  />
                  <IndustryInput
                    solutionId={formData.solutionId}
                    source='industry'
                    label={label('industry')}
                    disabled={!formData.legalName}
                    allowEmpty
                    fullWidth
                  />
                  <MerchantCategoryCodeInput
                    solutionId={formData.solutionId}
                    source='merchantCategoryCode'
                    label={label('merchantCategoryCode')}
                    disabled={!formData.legalName}
                    allowEmpty
                    fullWidth
                  />
                  <TextInput
                    source='description'
                    label={label('description')}
                    multiline
                    style={{ minHeight: '4em' }}
                    disabled={!formData.legalName}
                    fullWidth
                  />
                  <BranchSelect record={record} source='branch' fullWidth />
                </Fragment>
              );
            }}
          </FormDataConsumer>
        </EditForm>
      </Drawer>
    </Fragment>
  );
};

export default EditAccountInfo;
